import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Bell,
  BarChart3,
  CalendarClock,
  ClipboardCheck,
  FileClock,
  LogOut,
  Menu,
  Network,
  Route,
  Settings,
  ShieldCheck,
  Truck,
  UserCog,
  Users,
  X,
} from "lucide-react";
import { api, post } from "./api";
import {
  AccountsPage,
  AccessPage,
  BatchPage,
  DashboardPage,
  DailyReportsPage,
  JobsPage,
  ProxyPage,
  RoutesPage,
  SchedulesPage,
  SettingsPage,
  WaybillsPage,
} from "./pages";
import type { CurrentUser, Waybill } from "./types";
import { Badge, Button, Dialog, Field, IconButton, Input } from "./ui";

type Page =
  | "dashboard"
  | "accounts"
  | "waybills"
  | "batch"
  | "routes"
  | "schedules"
  | "reports"
  | "jobs"
  | "access"
  | "proxy"
  | "settings";

const navigation: Array<{ id: Page; label: string; icon: typeof Activity; superOnly?: boolean }> = [
  { id: "dashboard", label: "运行概览", icon: Activity },
  { id: "accounts", label: "账号管理", icon: Users },
  { id: "waybills", label: "运单中心", icon: Truck },
  { id: "batch", label: "批量操作", icon: ClipboardCheck },
  { id: "routes", label: "线路任务", icon: Route },
  { id: "schedules", label: "定时任务", icon: CalendarClock },
  { id: "reports", label: "每日汇总", icon: BarChart3 },
  { id: "jobs", label: "任务与日志", icon: FileClock },
  { id: "access", label: "用户与分组", icon: UserCog, superOnly: true },
  { id: "proxy", label: "代理 IP 池", icon: Network, superOnly: true },
  { id: "settings", label: "系统设置", icon: Settings, superOnly: true },
];

function AuthGate({ onAuthenticated }: { onAuthenticated: (user: CurrentUser) => void }) {
  const status = useQuery({
    queryKey: ["auth-status"],
    queryFn: () =>
      api<{ setupRequired: boolean; authenticated: boolean; user?: CurrentUser }>(
        "/api/auth/status",
      ),
  });
  const [username, setUsername] = useState("superadmin");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (status.data?.authenticated && status.data.user)
      onAuthenticated(status.data.user);
  }, [status.data?.authenticated, status.data?.user, onAuthenticated]);

  async function submit() {
    setBusy(true);
    setError("");
    try {
      if (status.data?.setupRequired)
        await post("/api/auth/setup", { password });
      const result = await post<{ user: CurrentUser }>("/api/auth/login", {
        username: status.data?.setupRequired ? "superadmin" : username,
        password,
      });
      onAuthenticated(result.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : "操作失败");
    } finally {
      setBusy(false);
    }
  }

  if (status.isLoading)
    return (
      <div className="auth-screen">
        <div className="auth-panel skeleton-panel" />
      </div>
    );
  return (
    <div className="auth-screen">
      <section className="auth-panel">
        <div className="auth-brand">
          <span className="brand-mark">
            <Truck size={24} />
          </span>
          <div>
            <strong>易达宝运营台</strong>
            <span>本机多账号业务控制台</span>
          </div>
        </div>
        <div className="auth-copy">
          <ShieldCheck size={28} />
          <h1>{status.data?.setupRequired ? "初始化超级管理员" : "管理账号登录"}</h1>
          <p>
            {status.data?.setupRequired
              ? "首次使用，请设置不少于 8 位的本机管理员密码。"
              : "登录后可查询账号、预检并执行真实业务操作。"}
          </p>
        </div>
        <Field label="用户名">
          <Input
            value={status.data?.setupRequired ? "superadmin" : username}
            onChange={(event) => setUsername(event.target.value)}
            disabled={status.data?.setupRequired}
            autoComplete="username"
          />
        </Field>
        <Field label={status.data?.setupRequired ? "管理员密码" : "密码"}>
          <Input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && submit()}
            autoComplete={status.data?.setupRequired ? "new-password" : "current-password"}
            autoFocus
          />
        </Field>
        {error && <div className="inline-error">{error}</div>}
        <Button onClick={submit} disabled={busy || password.length < 8}>
          {busy
            ? "处理中..."
            : status.data?.setupRequired
              ? "完成初始化"
              : "登录"}
        </Button>
        <p className="auth-footnote">
          服务默认仅监听 127.0.0.1，凭据使用本机密钥加密保存。
        </p>
      </section>
    </div>
  );
}

export default function App() {
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState<CurrentUser>();
  const [page, setPage] = useState<Page>(() => {
    const requested = new URLSearchParams(window.location.search).get("page") as Page | null;
    return navigation.some((item) => item.id === requested) ? requested! : "dashboard";
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [profileMessage, setProfileMessage] = useState("");
  const notifications = useQuery({
    queryKey: ["notifications"],
    queryFn: () =>
      api<
        Array<{
          id: string;
          level: string;
          title: string;
          message: string;
          read_at?: string;
          created_at: string;
        }>
      >("/api/notifications"),
    enabled: !!currentUser,
    refetchInterval: 30_000,
  });

  const selectedTargets = useMemo(
    () =>
      Array.from(selected).map((key) => {
        const [accountId, waybillId] = key.split("::");
        return { accountId, waybillId };
      }),
    [selected],
  );

  async function logout() {
    await post("/api/auth/logout");
    queryClient.clear();
    setPage("dashboard");
    setCurrentUser(undefined);
  }

  function navigate(next: Page) {
    setPage(next);
    setSidebarOpen(false);
    const params = new URLSearchParams(window.location.search);
    params.set("page", next);
    if (next !== "reports") {
      params.delete("reportId");
      params.delete("driverId");
    }
    if (next !== "accounts") {
      params.delete("accountId");
      params.delete("statusGroup");
    }
    if (next !== "routes") params.delete("routeRunId");
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
  }

  function openReports(planId?: string) {
    const params = new URLSearchParams(window.location.search);
    params.set("page", "reports");
    if (planId) params.set("planId", planId);
    params.delete("accountId");
    params.delete("statusGroup");
    params.delete("routeRunId");
    window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
    setPage("reports");
    setSidebarOpen(false);
  }

  function authenticatedAs(user: CurrentUser) {
    if (user.role !== "super_admin" && (page === "access" || page === "proxy" || page === "settings"))
      setPage("dashboard");
    setCurrentUser(user);
  }

  async function changePassword() {
    try {
      await post("/api/auth/change-password", {
        currentPassword,
        newPassword,
      });
      setProfileMessage("密码已更新，其他登录会话已经失效");
      setCurrentPassword("");
      setNewPassword("");
    } catch (error) {
      setProfileMessage(error instanceof Error ? error.message : "修改密码失败");
    }
  }

  useEffect(() => {
    if (!currentUser) return;
    if (
      typeof Notification === "undefined" ||
      Notification.permission !== "granted"
    )
      return;
    const newest = notifications.data?.find((item) => !item.read_at);
    if (newest)
      new Notification(newest.title, { body: newest.message, tag: newest.id });
  }, [currentUser, notifications.data]);

  if (!currentUser)
    return <AuthGate onAuthenticated={authenticatedAs} />;
  const visibleNavigation = navigation.filter(
    (item) => !item.superOnly || currentUser.role === "super_admin",
  );
  const unread =
    notifications.data?.filter((item) => !item.read_at).length ?? 0;

  return (
    <div className="app-shell">
      <aside className={`sidebar ${sidebarOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-brand">
          <span className="brand-mark">
            <Truck size={20} />
          </span>
          <div>
            <strong>易达宝运营台</strong>
            <span>YDB Operations</span>
          </div>
          <IconButton
            label="关闭导航"
            className="mobile-close"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={18} />
          </IconButton>
        </div>
        <nav className="sidebar-nav">
          {visibleNavigation.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className={page === item.id ? "nav-active" : ""}
                onClick={() => navigate(item.id)}
              >
                <Icon size={17} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="sidebar-footer">
          <div className="system-state">
            <span className="status-dot" />
            <div>
              <strong>本机服务正常</strong>
              <span>{currentUser.displayName || currentUser.username}</span>
            </div>
          </div>
          <button onClick={logout}>
            <LogOut size={16} />
            退出登录
          </button>
        </div>
      </aside>
      {sidebarOpen && (
        <button
          className="mobile-scrim"
          aria-label="关闭导航"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <main className="main-area">
        <header className="topbar">
          <div className="topbar-left">
            <IconButton
              label="打开导航"
              className="mobile-menu"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={19} />
            </IconButton>
            <div>
              <strong>
                {visibleNavigation.find((item) => item.id === page)?.label}
              </strong>
              <span>
                {new Intl.DateTimeFormat("zh-CN", {
                  dateStyle: "long",
                  timeZone: "Asia/Shanghai",
                }).format(new Date())}
              </span>
            </div>
          </div>
          <div className="topbar-actions">
            {selected.size > 0 && (
              <button
                className="selection-pill"
                onClick={() => navigate("batch")}
              >
                {selected.size} 张已选运单
              </button>
            )}
            <div className="notification-wrap">
              <IconButton
                label="通知"
                onClick={() => setNotificationOpen((value) => !value)}
              >
                <Bell size={18} />
                {unread > 0 && (
                  <span className="notification-count">{unread}</span>
                )}
              </IconButton>
              {notificationOpen && (
                <NotificationPanel
                  items={notifications.data ?? []}
                  onRead={async (id) => {
                    await post(`/api/notifications/${id}/read`);
                    notifications.refetch();
                  }}
                />
              )}
            </div>
            <Button variant="ghost" className="user-menu-button" onClick={() => setProfileOpen(true)}>
              <UserCog size={16} />
              {currentUser.displayName || currentUser.username}
            </Button>
          </div>
        </header>
        <div className="workspace">
          {page === "dashboard" && <DashboardPage navigate={navigate} />}
          {page === "accounts" && <AccountsPage currentUser={currentUser} openReports={openReports} />}
          {page === "waybills" && (
            <WaybillsPage
              selected={selected}
              setSelected={setSelected}
              openBatch={() => navigate("batch")}
            />
          )}
          {page === "batch" && (
            <BatchPage
              selected={selected}
              setSelected={setSelected}
              targets={selectedTargets}
              currentUser={currentUser}
            />
          )}
          {page === "routes" && <RoutesPage currentUser={currentUser} openSchedules={() => navigate("schedules")} />}
          {page === "schedules" && <SchedulesPage currentUser={currentUser} openReports={openReports} />}
          {page === "reports" && <DailyReportsPage currentUser={currentUser} />}
          {page === "jobs" && <JobsPage currentUser={currentUser} />}
          {page === "access" && currentUser.role === "super_admin" && <AccessPage />}
          {page === "proxy" && currentUser.role === "super_admin" && <ProxyPage />}
          {page === "settings" && currentUser.role === "super_admin" && <SettingsPage />}
        </div>
      </main>
      <Dialog open={profileOpen} onOpenChange={setProfileOpen} title="当前用户">
        <div className="profile-summary">
          <strong>{currentUser.displayName || currentUser.username}</strong>
          <span>{currentUser.username} · {currentUser.role === "super_admin" ? "超级管理员" : "子账号"}</span>
        </div>
        {profileMessage && <div className="notice-bar">{profileMessage}</div>}
        <Field label="当前密码">
          <Input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
        </Field>
        <Field label="新密码">
          <Input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
        </Field>
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setProfileOpen(false)}>关闭</Button>
          <Button disabled={currentPassword.length < 8 || newPassword.length < 8} onClick={changePassword}>修改密码</Button>
        </div>
      </Dialog>
    </div>
  );
}

function NotificationPanel({
  items,
  onRead,
}: {
  items: Array<{
    id: string;
    level: string;
    title: string;
    message: string;
    read_at?: string;
    created_at: string;
  }>;
  onRead: (id: string) => void;
}) {
  return (
    <div className="notification-panel">
      <div className="notification-header">
        <strong>通知</strong>
        <div>
          {typeof Notification !== "undefined" &&
            Notification.permission !== "granted" && (
              <button
                className="notification-enable"
                onClick={() => Notification.requestPermission()}
              >
                启用桌面通知
              </button>
            )}
          <Badge
            tone={items.some((item) => !item.read_at) ? "warning" : "neutral"}
          >
            {items.filter((item) => !item.read_at).length} 未读
          </Badge>
        </div>
      </div>
      <div className="notification-list">
        {items.length === 0 && (
          <span className="notification-empty">暂无通知</span>
        )}
        {items.slice(0, 12).map((item) => (
          <button
            key={item.id}
            className={item.read_at ? "read" : ""}
            onClick={() => onRead(item.id)}
          >
            <span className={`notice-level notice-${item.level}`} />
            <div>
              <strong>{item.title}</strong>
              <p>{item.message}</p>
              <time>{item.created_at}</time>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function selectedKey(waybill: Pick<Waybill, "accountId" | "waybillId">) {
  return `${waybill.accountId}::${waybill.waybillId}`;
}
